import { Box, Flex, H2, Switch } from '@ouster/webviz-react-components';

import {
  differenceInDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { useMemo, useState } from 'react';
import { MdBusiness, MdPeople, MdCloudUpload } from 'react-icons/md';
import ChartCard from 'src/components/adminDashboard/growTrend/ChartCard';
import { DateRangePicker } from 'src/components/adminDashboard/growTrend/DateRangePicker';
import SummaryCard from 'src/components/adminDashboard/growTrend/SummaryCard';
import {
  useAdminDashboardOrganizationSummary,
  useAdminDashboardUserSummary,
  useAdminDashboardOrganizationTotal,
  useAdminDashboardUserTotal,
  useActiveUserCounts,
  useActiveUserDetails,
  useUploadCounts,
  useUploadDetails,
  useAllTimeUploadTotal,
  useExportOrganizationCsv,
  useExportUserCsv,
} from 'src/components/adminDashboard/useAdminDashboard';

export default function InsightGrowthTrends() {
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 6)), // Default to last 6 months
    end: new Date(),
    label: 'Last 6 Months',
  });
  const [includeOuster, setIncludeOuster] = useState(false);

  // All-time totals (lightweight endpoints, no date range)
  const {
    data: allTimeOrganizationData,
    isLoading: isLoadingAllTimeOrganizations,
  } = useAdminDashboardOrganizationTotal();

  const { data: allTimeUserData, isLoading: isLoadingAllTimeUsers } =
    useAdminDashboardUserTotal();

  // Date-range filtered data for charts
  const {
    data: organizationSummaryData,
    isLoading: isLoadingOrganizationSummary,
  } = useAdminDashboardOrganizationSummary(
    format(dateRange.start, 'yyyy-MM-dd'),
    format(dateRange.end, 'yyyy-MM-dd')
  );

  const { data: userSummaryData, isLoading: isLoadingUserSummary } =
    useAdminDashboardUserSummary(
      format(dateRange.start, 'yyyy-MM-dd'),
      format(dateRange.end, 'yyyy-MM-dd'),
      includeOuster
    );

  const { data: activeUserCountsData, isLoading: isLoadingActiveUserCounts } =
    useActiveUserCounts(
      format(dateRange.start, 'yyyy-MM-dd'),
      format(dateRange.end, 'yyyy-MM-dd'),
      !includeOuster,
      'month'
    );

  const { data: uploadCountsData, isLoading: isLoadingUploadCounts } =
    useUploadCounts(
      format(dateRange.start, 'yyyy-MM-dd'),
      format(dateRange.end, 'yyyy-MM-dd'),
      !includeOuster
    );

  const { data: allTimeUploadData, isLoading: isLoadingAllTimeUploads } =
    useAllTimeUploadTotal();

  const rangeUploadTotal = useMemo(() => {
    if (!uploadCountsData) return null;

    return Object.values(uploadCountsData).reduce(
      (sum, count) => sum + count,
      0
    );
  }, [uploadCountsData]);

  const formattedRangeLabel = useMemo(() => {
    if (dateRange.label) {
      return dateRange.label;
    }
    return `${format(dateRange.start, 'yyyy-MM-dd')} ~ ${format(
      dateRange.end,
      'yyyy-MM-dd'
    )}`;
  }, [dateRange]);

  const { refetch: exportActiveUserDetailsCsv } = useActiveUserDetails(
    format(dateRange.start, 'yyyy-MM-dd'),
    format(dateRange.end, 'yyyy-MM-dd'),
    false
  );

  const { refetch: exportUploadDetailsCsv } = useUploadDetails(
    format(dateRange.start, 'yyyy-MM-dd'),
    format(dateRange.end, 'yyyy-MM-dd'),
    !includeOuster,
    false
  );

  const { refetch: exportOrganizationCsv } = useExportOrganizationCsv(
    format(dateRange.start, 'yyyy-MM-dd'),
    format(dateRange.end, 'yyyy-MM-dd'),
    false
  );

  const { refetch: exportUserCsv } = useExportUserCsv(
    format(dateRange.start, 'yyyy-MM-dd'),
    format(dateRange.end, 'yyyy-MM-dd'),
    includeOuster
  );

  const organizationData = useMemo(() => {
    if (!organizationSummaryData?.growth_series) return [];

    return organizationSummaryData.growth_series.map((point) => {
      const date = parseISO(point.date);
      return {
        timestamp: date.getTime(),
        fullDate: format(date, 'MMM dd, yyyy'),
        newOrgs: point.new_orgs,
        totalOrgs: point.total_orgs,
      };
    });
  }, [organizationSummaryData]);

  const userData = useMemo(() => {
    if (!userSummaryData?.growth_series) return [];

    return userSummaryData.growth_series.map((point) => {
      const date = parseISO(point.date);
      return {
        timestamp: date.getTime(),
        fullDate: format(date, 'MMM dd, yyyy'),
        newUsers: point.new_users,
        totalUsers: point.total_users,
      };
    });
  }, [userSummaryData]);

  const activeUserCountData = useMemo(() => {
    const monthStart = startOfMonth(dateRange.start);
    const monthEnd = startOfMonth(dateRange.end);
    const monthlyCounts = activeUserCountsData ?? {};

    return eachMonthOfInterval({ start: monthStart, end: monthEnd }).map(
      (monthDate) => {
        const monthKey = format(monthDate, 'yyyy-MM');

        return {
          timestamp: monthDate.getTime(),
          fullDate: format(monthDate, 'MMM yyyy'),
          activeUsers: monthlyCounts[monthKey] ?? 0,
        };
      }
    );
  }, [activeUserCountsData, dateRange]);

  const uploadCountData = useMemo(() => {
    if (!uploadCountsData) return [];

    return Object.entries(uploadCountsData)
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([dateKey, count]) => {
        const date = parseISO(dateKey);

        return {
          timestamp: date.getTime(),
          fullDate: format(date, 'MMM dd, yyyy'),
          uploads: count,
        };
      });
  }, [uploadCountsData]);

  const sharedTicks = useMemo(() => {
    const { start, end } = dateRange;

    const totalDays = differenceInDays(end, start);

    // same day: just return the single day
    if (totalDays === 0) {
      return [start.getTime()];
    }
    // <= 14 days: every day
    if (totalDays <= 14) {
      return eachDayOfInterval({ start, end }).map((d) => d.getTime());
    }
    // <= 60 days: every 3 days
    if (totalDays <= 60) {
      return eachDayOfInterval({ start, end })
        .filter((_, i) => i % 3 === 0)
        .map((d) => d.getTime());
    }
    // <= 180 days: every 2 weeks
    if (totalDays <= 180) {
      return eachWeekOfInterval({ start, end })
        .filter((_, i) => i % 2 === 0)
        .map((d) => d.getTime());
    }
    // <= 365 days: every month
    if (totalDays <= 365) {
      return eachMonthOfInterval({ start, end }).map((d) => d.getTime());
    }
    // > 365 days: every 3 months
    return eachMonthOfInterval({ start, end })
      .filter((_, i) => i % 3 === 0)
      .map((d) => d.getTime());
  }, [dateRange]);

  const tickFormatter = useMemo(() => {
    const totalDays = differenceInDays(dateRange.end, dateRange.start);
    // if (totalDays <= 14) return (v: number) => format(new Date(v), 'MMM d');
    // if (totalDays <= 60) return (v: number) => format(new Date(v), 'MMM d');
    if (totalDays <= 180) return (v: number) => format(new Date(v), 'MMM d');
    return (v: number) => format(new Date(v), 'MMM yyyy');
  }, [dateRange]);

  const downloadOrganizationData = () => {
    exportOrganizationCsv().catch(() => undefined);
  };

  const downloadUserData = () => {
    exportUserCsv().catch(() => undefined);
  };

  const downloadActiveUserCountsData = () => {
    exportActiveUserDetailsCsv().catch(() => undefined);
  };

  const downloadUploadCountsData = () => {
    exportUploadDetailsCsv().catch(() => undefined);
  };

  return (
    <Box
      p="25px 36px 0px"
      h="full"
      display="flex"
      flexDirection="column"
      gap="1rem"
      overflow="hidden"
    >
      <Flex placeContent="space-between" alignItems="center" flexShrink={0}>
        <H2 fontWeight="medium" fontSize={30}>
          Growth Trends
        </H2>
        <Flex gap="1.5rem" alignItems="center">
          <Flex alignItems="center" gap="0.5rem">
            <Box fontWeight="medium" color="gray.300">
              Exclude Ouster Users
            </Box>
            <Switch
              isChecked={!includeOuster}
              onChange={(e) => setIncludeOuster(!e.target.checked)}
              data-testid="dashboard-exclude-ouster-users-switch"
              sx={{
                'span.chakra-switch__track[data-checked]': {
                  backgroundColor: 'blue.400',
                },
              }}
            />
          </Flex>
          <DateRangePicker range={dateRange} onChange={setDateRange} />
        </Flex>
      </Flex>
      <Box flex="1" overflowY="auto" minH={0} pr="4px">
        {/* Summary Cards Section */}
        <Flex gap="2rem" mt={6} flexWrap="wrap">
          <SummaryCard
            title="Total Organizations"
            icon={<MdBusiness />}
            allTimeValue={allTimeOrganizationData?.total_organizations ?? null}
            rangeValue={organizationSummaryData?.total_organizations ?? null}
            isLoadingAllTime={isLoadingAllTimeOrganizations}
            isLoadingRange={isLoadingOrganizationSummary}
            rangeLabel={formattedRangeLabel}
          />
          <SummaryCard
            title="Total Users"
            icon={<MdPeople />}
            allTimeValue={allTimeUserData?.total_users ?? null}
            rangeValue={userSummaryData?.total_users ?? null}
            isLoadingAllTime={isLoadingAllTimeUsers}
            isLoadingRange={isLoadingUserSummary}
            rangeLabel={formattedRangeLabel}
          />
          <SummaryCard
            title="Total Uploads"
            icon={<MdCloudUpload />}
            allTimeValue={allTimeUploadData?.total_uploads ?? null}
            rangeValue={rangeUploadTotal}
            isLoadingAllTime={isLoadingAllTimeUploads}
            isLoadingRange={isLoadingUploadCounts}
            rangeLabel={formattedRangeLabel}
          />
        </Flex>

        {/* Main Chart Section */}
        <Flex gap="2rem" mt={6} flexWrap="wrap">
          <ChartCard
            title="Organization Growth (Cumulative)"
            data={organizationData}
            ticks={sharedTicks}
            tickFormatter={tickFormatter}
            dataKey="totalOrgs"
            displayName="Total Organizations"
            isLoading={isLoadingOrganizationSummary}
            downloadHandler={downloadOrganizationData}
          />
          <ChartCard
            title="User Growth (Cumulative)"
            data={userData}
            ticks={sharedTicks}
            tickFormatter={tickFormatter}
            dataKey="totalUsers"
            displayName="Total Users"
            isLoading={isLoadingUserSummary}
            downloadHandler={downloadUserData}
          />
        </Flex>
        <Flex gap="2rem" mt={6} mb={6} flexWrap="wrap">
          <ChartCard
            title="Active User Count (monthly)"
            data={activeUserCountData}
            dataKey="activeUsers"
            displayName="Active Users"
            isLoading={isLoadingActiveUserCounts}
            downloadHandler={downloadActiveUserCountsData}
            chartType="bar"
          />
          <ChartCard
            title="Upload Count (daily)"
            data={uploadCountData}
            ticks={sharedTicks}
            tickFormatter={tickFormatter}
            dataKey="uploads"
            displayName="Uploads"
            isLoading={isLoadingUploadCounts}
            downloadHandler={downloadUploadCountsData}
          />
        </Flex>
      </Box>
    </Box>
  );
}
